import { useMsal } from '@azure/msal-react'
import { faExpand } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Box,
  Button,
  Checkbox,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import React, { useEffect, useState } from 'react'
import {
  getTransactionsPendingContentCheck,
  updateApprovalStatuses,
} from '../../data/admin.service'
import { TransactionPendingApproval } from '../../models/Admin/PendingApprovalItems'
import { COLORS } from '../../utils/constants'
import ContentCheckSkeleton from './ContentCheckSkeleton'

const AdsRequiringContentCheck: React.FC = () => {
  const { instance } = useMsal()
  const [pendingApprovalItems, setPendingApprovalItems] = useState<
    TransactionPendingApproval[]
  >([])
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)

  useEffect(() => {
    const fetchItems = async () => {
      setIsLoading(true)
      try {
        const adsPendingContentCheck =
          await getTransactionsPendingContentCheck(instance)
        setPendingApprovalItems(adsPendingContentCheck)
      } catch (error) {
        console.error('Error fetching items:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchItems()
  }, [instance])

  const handleApprovalClick = (isApproved: boolean, deviceAdId: number) => {
    setPendingApprovalItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        deviceAdvertisements: item.deviceAdvertisements.map((ad) =>
          ad.id === deviceAdId ? { ...ad, isApproved: isApproved } : ad,
        ),
      })),
    )
  }

  const handleDenialReasonUpdate = (
    denialReason: string,
    deviceAdId: number,
  ) => {
    setPendingApprovalItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        deviceAdvertisements: item.deviceAdvertisements.map((ad) =>
          ad.id === deviceAdId ? { ...ad, denialReason: denialReason } : ad,
        ),
      })),
    )
  }

  const handleSubmit = async (transaction: TransactionPendingApproval) => {
    setLoadingId(transaction.paymentIntentId)
    try {
      const regularAds = transaction.deviceAdvertisements.filter(
        (da) => !da.isAllLocationsBundle,
      )
      const bundleAds = transaction.deviceAdvertisements.filter(
        (da) => da.isAllLocationsBundle,
      )
      await updateApprovalStatuses(
        {
          paymentIntentId: transaction.paymentIntentId,
          transactionId: transaction.transactionId,
          deviceAdvertisementApprovalStatuses: regularAds.map((da) => ({
            deviceAdvertisementId: da.id,
            isApproved: da.isApproved,
            denialReason: da.denialReason,
          })),
          allLocationsBundleApprovalStatuses: bundleAds.map((da) => ({
            advertisementId: da.advertisementId!,
            isApproved: da.isApproved,
            denialReason: da.denialReason,
          })),
        },
        instance,
      )
      setPendingApprovalItems((prev) =>
        prev.filter((i) => i.paymentIntentId !== transaction.paymentIntentId),
      )
      notifications.show({
        title: 'Success',
        message: 'Successfully updated!',
        color: 'green',
        autoClose: true,
      })
    } catch (error) {
      console.error('Error updating approval statuses:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to update. Please try again.',
        color: 'red',
        autoClose: true,
      })
    } finally {
      setLoadingId(null)
    }
  }

  if (isLoading) {
    return <ContentCheckSkeleton />
  }

  return (
    <Box py={24}>
      <Title>Ads requiring content check</Title>
      {pendingApprovalItems.length === 0 && (
        <Paper withBorder p={36} mt={32} bg={COLORS.LightGray} radius={16}>
          <Text>No advertisements requiring content check</Text>
        </Paper>
      )}
      {pendingApprovalItems.map((i) => (
        <Paper
          withBorder
          key={i.paymentIntentId}
          id={i.paymentIntentId}
          p={36}
          mt={32}
          bg={COLORS.LightGray}
          radius={16}
        >
          <Stack gap={20}>
            <Group justify='space-between'>
              <Group>
                <Text>Advertiser: </Text>
                <Title order={4}>{i.organizationName}</Title>
              </Group>
              <Text>{i.checkoutDate}</Text>
            </Group>
            <Stack>
              {i.deviceAdvertisements.map((da) => (
                <Paper
                  withBorder
                  shadow='md'
                  h='auto'
                  mih={da.isAllLocationsBundle ? undefined : 400}
                  p={24}
                  radius={16}
                  key={
                    da.isAllLocationsBundle
                      ? `bundle-${da.advertisementId}`
                      : da.id
                  }
                >
                  <Group
                    justify='space-between'
                    align='flex-start'
                    h='100%'
                    gap='xl'
                  >
                    <Group h='100%' gap='lg' align='flex-start' wrap='wrap'>
                      {da.adImageUrl && (
                        <div
                          style={{
                            position: 'relative',
                            minWidth: 200,
                            maxWidth: 311,
                          }}
                        >
                          <AspectRatio ratio={16 / 9} w='100%'>
                            <Image src={da.adImageUrl} radius='md' />
                          </AspectRatio>
                          <ActionIcon
                            variant='filled'
                            radius='md'
                            onClick={() => setExpandedImage(da.adImageUrl)}
                            style={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faExpand}
                              style={{ fontSize: '14px', color: 'white' }}
                            />
                          </ActionIcon>
                        </div>
                      )}
                      <Stack style={{ minWidth: 200, flex: 1 }}>
                        {da.isAllLocationsBundle ? (
                          <>
                            <Text fz={24} fw={600}>
                              All Locations Bundle
                            </Text>
                            <Text fz={20} c='dimmed'>
                              {da.locationCount} location
                              {da.locationCount !== 1 ? 's' : ''}
                            </Text>
                            <Text fz={24}>{da.advertisementName}</Text>
                          </>
                        ) : (
                          <>
                            <Text fz={24} fw={600}>
                              Host: {da.locationName}
                            </Text>
                            <Text fz={24}>{da.deviceName}</Text>
                            <Text fz={24}>{da.advertisementName}</Text>
                          </>
                        )}
                      </Stack>
                    </Group>
                    <Stack miw={300} align='flex-start'>
                      <Checkbox
                        checked={da.isApproved}
                        label='Approved?'
                        size='xl'
                        onChange={(event) =>
                          handleApprovalClick(
                            event.currentTarget.checked,
                            da.isAllLocationsBundle
                              ? -da.advertisementId!
                              : da.id,
                          )
                        }
                      />
                      {!da.isApproved && (
                        <Textarea
                          w='100%'
                          value={da.denialReason}
                          rows={4}
                          label='Denial Reason'
                          onChange={(e) =>
                            handleDenialReasonUpdate(
                              e.target.value,
                              da.isAllLocationsBundle
                                ? -da.advertisementId!
                                : da.id,
                            )
                          }
                        />
                      )}
                    </Stack>
                  </Group>
                </Paper>
              ))}
            </Stack>
            <Group justify='flex-end'>
              <Button
                w={100}
                onClick={() => handleSubmit(i)}
                disabled={loadingId != null}
                bd='1px solid lightgray'
              >
                {loadingId === i.paymentIntentId ? (
                  <Loader type='dots' color='black' size='sm' />
                ) : (
                  'Submit'
                )}
              </Button>
            </Group>
          </Stack>
        </Paper>
      ))}

      {/* Fullscreen Image Modal */}
      <Modal
        opened={!!expandedImage}
        onClose={() => setExpandedImage(null)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          content: {
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          inner: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'black',
          }}
        >
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setExpandedImage(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
            }}
          >
            <FontAwesomeIcon icon={faExpand} />
          </ActionIcon>
          {expandedImage && (
            <div
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 'calc(90vh * 16/9)',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={expandedImage}
                alt='Advertisement'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </Modal>
    </Box>
  )
}

export default AdsRequiringContentCheck
